import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AppState } from 'src/app/app.states';
import { bankAccountById } from 'src/app/features/bank-accounts/state/bank-accounts.selectors';
import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { Currency } from '../enums/Currency.enum';

export interface DepositLimitError {
  remaining: number;
  currency: string;
}

@Injectable({ providedIn: 'root' })
export class DepositLimitValidator {
  constructor(private store: Store<AppState>) {}

  withinTierLimit(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const amount = Number(control.value);

      // An empty or malformed amount belongs to `required` / `MoneyValidator` /
      // `GreaterThanZeroValidator`. Claiming it here too would show the investor a limit
      // error for a field they have not filled in.
      if (!Number.isFinite(amount) || amount <= 0) {
        return null;
      }

      const allowance = this.remainingAllowance();

      // No allowance on the profile means either an uncapped tier or a profile that
      // predates the field. Neither is this validator's business — and the endpoint is
      // still the control, so staying quiet cannot open a door.
      if (!allowance || allowance.hasLimit !== true) {
        return null;
      }

      const currency = this.selectedCurrency(control.parent);

      if (!currency) {
        return null;
      }

      const remaining =
        currency === Currency.USD ? allowance.remainingUsd : allowance.remainingPen;

      if (!Number.isFinite(remaining) || amount <= remaining) {
        return null;
      }

      return { depositLimitExceeded: { remaining, currency } as DepositLimitError };
    };
  }

  private remainingAllowance(): any {
    let allowance = null;

    this.store
      .select(UsersSelectors.profile())
      .pipe(take(1))
      .subscribe(profile => (allowance = profile && profile.depositAllowance));

    return allowance;
  }

  private selectedCurrency(parent: AbstractControl): string {
    const bankAccountId = parent.get('bankAccount') && parent.get('bankAccount').value;

    if (!bankAccountId) {
      return null;
    }

    let currency = null;

    this.store
      .select(bankAccountById(bankAccountId))
      .pipe(take(1))
      .subscribe(bankAccount => (currency = bankAccount && bankAccount.currency));

    return currency;
  }
}
